import { useEffect, useState } from "react";
import { useGlobalContext } from "../context/globalContext";
import { Toaster, toast } from "react-hot-toast";
import Loading from "../components/Loading";
import TableRenderer from "../components/TableRenderer";
import axios from 'axios';

function Unresolved() {
    const { userInfo, queryType, setQueryType } = useGlobalContext();
    const [data, setData] = useState(null);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);

    const getFilteredColumns = (data) => {
        // Columns to always show
        const alwaysShowColumns = [
            "status",  // Added status to always show first
            "date",
            "name",
            "email",
            "number",
            "query_desc",
            "taken_by",
            "comment"
        ];

        // Updated column order - status first, removed lastUpdated
        const columnOrder = [
            "status",         // Ensuring status is first
            "query_type",
            "id",
            "date",
            "name",
            "new_name",
            "email",
            "new_email",
            "number",
            "new_number",
            "amount",
            "utr",
            "current_batch",
            "new_batch",
            "current_course",
            "new_course",
            "newCourseId",
            "content_desc",
            "reason",
            "feedback",
            "first_installment",
            "second_installment",
            "deviceName",
            "deviceSpecs",
            "file",
            "query_desc",
            "taken_by",
            "comment"
        ];

        // Get unique columns from the data
        const uniqueColumns = Array.from(
            new Set(data.flatMap((response) => Object.keys(response)))
        ).map((key) => {
            const col = {
                id: key,
                header: key,
                accessorKey: key,
                footer: key,
            };
            if (key === 'date') {
                col.sortingFn = (rowA, rowB) => {
                    const dateA = rowA.original.timestamp ? new Date(rowA.original.timestamp) : new Date(0);
                    const dateB = rowB.original.timestamp ? new Date(rowB.original.timestamp) : new Date(0);
                    return dateA - dateB;
                };
            }
            return col;
        });

        // Sort columns based on the desired order
        const sortedColumns = uniqueColumns.sort(
            (a, b) => {
                const indexA = columnOrder.indexOf(a.id);
                const indexB = columnOrder.indexOf(b.id);
                // If column isn't in columnOrder, put it at the end
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            }
        );

        // Filter out columns with all empty values and remove unwanted columns
        let filteredColumns = sortedColumns.filter((column) => {
            // List of columns to explicitly remove
            const columnsToRemove = [
                "id",
                "query_type",
                "type",
                "createdAt",
                "lastUpdated",     // Explicitly remove lastUpdated
                "last_updated_by", // Remove this too just in case
                "timestamp",
                "original_id"
            ];

            // Remove unwanted columns
            if (columnsToRemove.includes(column.id)) {
                return false;
            }

            // Keep columns that are in alwaysShowColumns
            if (alwaysShowColumns.includes(column.id)) {
                return true;
            }

            // For other columns, check if they have any non-empty values
            return data.some(response => {
                const value = response[column.id];
                return value !== null && value !== "" && value !== undefined;
            });
        });

        // Remove taken_by column for non-admin users
        if (!userInfo?.isAdmin) {
            filteredColumns = filteredColumns.filter(
                (column) => column.id !== "taken_by"
            );
        }

        // Add before the actions column
        const emailColumn = {
            id: "sendEmail",
            header: "Send Email",
            accessorKey: "sendEmail",
            footer: "Send Email",
            cell: ({ row }) => {
                // Only show for numberchange and emailchange
                if (row.original.query_type !== "numberchange" && row.original.query_type !== "emailchange") {
                    return null;
                }

                return (
                    <td className="email-cell" key={`email-${row.id}`}>
                        <button
                            onClick={() => handleSendEmail(row.original)}
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded"
                        >
                            <i className="fa-solid fa-envelope mr-1"></i>
                            Send Email
                        </button>
                    </td>
                );
            },
        };

        // Add the email column before actions
        return [...filteredColumns, emailColumn];


    };
    const handleDelete = async (row) => {
        toast.loading("Deleting row...");

        try {
            const response = await axios.delete(
                "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/queries",
                {
                    data: {
                        queryType: queryType,
                        id: row.id,
                        timestamp: row.timestamp,
                        status: "PENDING"
                    }
                }
            );
            console.log(response.data);
            if (!response.data) {
                throw new Error(`${response.type} error! Status: ${response.status}`);
            }
            getData({ withToast: false });
            toast.dismiss();
            toast.success("Deleted !");
        } catch (error) {
            //clear toast
            toast.dismiss();
            toast.error(error.message);
            console.error("error:", error);
        }
    };
    const handleSendEmail = async (rowData) => {
        toast.loading("Sending email...");

        try {
            const emailData = {
                queryType: rowData.query_type,
                studentName: rowData.name,
                oldValue: rowData.query_type === "numberchange" ? rowData.number : rowData.email,
                newValue: rowData.query_type === "numberchange" ? rowData.new_number : rowData.new_email
            };

            const response = await axios.post(
                "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/email/send-email",
                emailData
            );

            if (!response.data) {
                throw new Error(`${response.type} error! Status: ${response.status}`);
            }

            toast.dismiss();
            toast.success("Email sent successfully!");
        } catch (error) {
            toast.dismiss();
            toast.error(error.message || "Failed to send email");
            console.error("Error:", error);
        }
    };

    const getData = async ({ withToast }) => {
        setLoading(true);
        setData(null);
        console.log("Fetching data...");
        try {
            const response = await axios.post(
                "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/api/getqueries",
                { queryType: queryType, status: "PENDING" }
            );

            if (!response.data) {
                throw new Error(`Response error! Status: ${response.status}`);
            }

            const data = response.data;
            console.log(data)
            // Process timestamps and ensure date formatting
            const processedData = data.map((each) => {
                // Format timestamp to desired date format
                if (each.timestamp) {
                    const dateObject = new Date(each.timestamp);
                    const day = String(dateObject.getDate()).padStart(2, "0");
                    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
                    const year = String(dateObject.getFullYear()).slice(2);
                    each.date = `${day}/${month}/${year}`; // Format as DD/MM/YY
                } else {
                    each.date = "N/A"; // Fallback if timestamp is missing or invalid
                }

                // Remove unwanted fields (keep timestamp for sorting and lookup)
                const { original_id, ...filteredData } = each;
                return filteredData;
            });

            // Sort data based on timestamp in ascending order (oldest top, latest bottom)
            processedData.sort((a, b) => {
                const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
                const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
                return dateA - dateB;
            });

            // Filter for pending status
            let sortedData = processedData.filter((row) => row.status === "PENDING");

            if (!userInfo?.isAdmin) {
                sortedData = sortedData.filter((row) => row.taken_by === userInfo.email);
            }

            setData(sortedData);
            setColumns(getFilteredColumns(sortedData));

            if (withToast) {
                toast.dismiss();
                toast.success("Data fetched successfully");
            }
        } catch (err) {
            toast.error(err.message || "Error fetching data");
            console.error("Error:", err);
        } finally {
            setLoading(false);
        }
    };


    const updateRow = (rowId, updatedFields) => {
        setData(prevData =>
            prevData.map(row =>
                row.id === rowId ? { ...row, ...updatedFields } : row
            )
        );
    };

    useEffect(() => {
        //set the query type to the query in local storage
        const query = localStorage.getItem("queryType");
        //if query is not null then set the query type to the query in local storage
        if (query) {
            setQueryType(query);
        } else {
            setQueryType("nameChange");
            localStorage.setItem("queryType", "nameChange");
        }
        if (userInfo?.email) getData({ withToast: true });
    }, [userInfo?.email, queryType]);

    return (
        <div className="p-2 ">
            <div className="flex flex-row gap-3 items-center justify-center  w-fit ">
                <label
                    htmlFor="query-type "
                    className="w-fit text-left pl-1 text-lg font-semibold "
                >
                    Select Query type:
                </label>

                <select
                    className="float-left  border-2 border-theme-yellow-dark inline px-3 py-2 rounded-md  text-black outline-none w-fit  cursor-pointer"
                    value={queryType}
                    onChange={(e) => {
                        setQueryType(e.target.value);
                        localStorage.setItem("queryType", e.target.value);
                    }}
                >
                    <option value="nameChange">Name Change</option>
                    <option value="batchShift">Batch Shift</option>
                    <option value="emi">EMI</option>
                    <option value="refund">Refund</option>
                    <option value="removeCourseAccess">Remove Course Access</option>
                    <option value="feedback">Feedback</option>

                    <option value="numberchange">Number change</option>
                    <option value="emailchange">Email change</option>
                    <option value="contentmissing">Content Missing</option>
                    <option value="coursenotvisible">Course Not Visible</option>
                    <option value="UPIpayment">UPI Payment</option>
                    <option value="grpnotalloted">Group not alloted</option>
                    <option value="technicalIssue">Technical Issue</option>

                    <option value="misc">Misc</option>
                </select>
            </div>
            <Toaster
                position="bottom-left"
                toastOptions={{
                    // Define default options
                    className: "",

                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },

                    // Default options for specific types
                    success: {
                        duration: 2000,
                        theme: {
                            primary: "green",
                            secondary: "black",
                        },
                    },
                }}
            />

            {loading && !data ? (
                <Loading />
            ) : data?.length == 0 ? (
                <p className="text-xl  mt-5 text-center w-full">
                    No Data of the selected type found.
                </p>
            ) : (
                data &&
                columns && (
                    <TableRenderer updateRow={updateRow}
                        data={data} columns={columns} getData={getData} handleDelete={handleDelete} showEdit={true} defaultSortDesc={false} />
                )
            )}
        </div>
    );
}

export default Unresolved;
